import PropTypes from 'prop-types';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import * as React from 'react';
import { useState } from 'react';
import { Skeleton } from '@mui/material';
import { getContentThumbnailUrl } from 'utils/entityUtils';
import { formatDate } from 'utils/formatTime';
import ChipsList from 'components/Lists/ChipsList';
import app from 'config/app.json';
import ScorePercentWithValueLabel from 'pages/fileViewer/components/carousel/ScorePercentWithValueLabel';
import Box from '@mui/material/Box';

/**
 * @function RelatedDocumentItem
 * @description Component to display a related document item with thumbnail, title, tags, score, and date.
 * @param {object} props - The component props.
 * @param {object} props.relatedDocument - The related document details.
 * @param {function} [props.onDocumentClick] - Callback when the document is clicked.
 * @param {bool} [props.scoreDisplayed] - is the score displayed ?
 * @param {number} [props.maxScore] - the max score
 * @returns {React.Node} The rendered component.
 */
function RelatedDocumentItem({ relatedDocument, onDocumentClick, scoreDisplayed, maxScore }) {
  const { entity, tags, score, files } = relatedDocument;
  const thumbnailUrl = getContentThumbnailUrl(entity.id);
  const [renditionLoaded, setRenditionLoaded] = useState(false);

  const ellipsis3 = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: '3',
    WebkitBoxOrient: 'vertical'
  };

  return (
    <MDBox
      p={2}
      bgColor="container"
      borderRadius="md"
      display="flex"
      flexDirection="column"
      sx={{
        '&:hover': {
          transition: 'background 1s ease-out',
          background: ({ functions: { linearGradient }, palette: { gradients } }) =>
            `${linearGradient(gradients.hover.main, gradients.hover.state)}`
        },
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'center', sm: 'stretch' }
      }}
    >
      <MDBox
        onClick={() => onDocumentClick(files[0])}
        shadow="md"
        width="200px"
        height="280px"
        sx={{ cursor: 'pointer' }}
      >
        {renditionLoaded ? null : (
          <Skeleton variant="rectangular" animation="wave" width={200} height={280} />
        )}
        <img
          loading="lazy"
          style={renditionLoaded ? { width: '200px' } : { width: '0' }}
          src={thumbnailUrl}
          width="200px"
          height="280px"
          alt="thumbnail"
          onLoad={() => setRenditionLoaded(true)}
        />
      </MDBox>
      <MDBox
        display="flex"
        flexDirection="column"
        flex
        mx={1}
        sx={{ alignItems: 'space-between', gap: '12px', flex: '1', width: '100%' }}
      >
        <MDBox display="flex" flexDirection="column" sx={{ flex: 1 }}>
          <MDBox display="flex">
            <MDBox
              sx={{ display: 'flex', flexDirection: 'column', marginTop: { xs: '12px', sm: '0' } }}
            >
              <MDTypography variant="button" sx={{ fontWeight: 'bold', ...ellipsis3 }}>
                {entity.name}
              </MDTypography>
              <ChipsList tags={tags} my={1} onChipClicked={() => {}} />
            </MDBox>
          </MDBox>
          <MDBox
            sx={{ marginTop: 'auto', alignItems: 'center', justifyItems: 'center' }}
            display="flex"
          >
            {scoreDisplayed ? (
              <Box sx={{ flex: '1' }}>
                <ScorePercentWithValueLabel
                  variant="determinate"
                  score={computeAdjustedPercentage(score)}
                />
              </Box>
            ) : null}

            <MDTypography
              variant="button"
              fontWeight="regular"
              color="text"
              sx={{ marginLeft: 0.5, textAlign: 'right' }}
            >
              {formatDate(entity.publicationDate, app.format.date.display)}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );

  function computeAdjustedPercentage(score) {
    const percentage = Math.round((score / maxScore) * 100);
    return percentage > 10 ? percentage : 10;
  }
}

RelatedDocumentItem.defaultProps = {
  onDocumentClick: () => {},
  scoreDisplayed: false
};

RelatedDocumentItem.propTypes = {
  relatedDocument: PropTypes.shape({
    entity: PropTypes.object.isRequired,
    tags: PropTypes.array.isRequired,
    score: PropTypes.number.isRequired,
    files: PropTypes.array.isRequired
  }).isRequired,
  onDocumentClick: PropTypes.func, // Expected: (file: object) => void
  scoreDisplayed: PropTypes.bool,
  maxScore: PropTypes.number
};

export default RelatedDocumentItem;
